import React, { useState, useContext, useEffect } from 'react';
import { Box, Button, Card, Radio, FormControlLabel, IconButton, CircularProgress } from '@mui/material';
import LayoutAccessManagement from 'components/structure/LayoutAccessManagement';
import { useNavigate, useLocation } from 'react-router-dom';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { SnackbarContext } from "context/snackBarContext";
import RoleService from "services/RoleService";

const CountrySelection = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { state } = location;
    const { setOpenAlert, setSeverity, setMessage } = useContext(SnackbarContext);
    const [loading, setLoading] = useState(false);
    const [countries, setCountries] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [expanded, setExpanded] = useState({});
    const [fetchLoading, setFetchLoading] = useState(true);
    useEffect(() => {
        const fetchCountryFunctionality = async () => {
            if (state?.roleName) {
                try {
                    setFetchLoading(true);
                    let response = await RoleService.getCountryFunctionality(state.roleName);


                    // Transform API response to required format
                    const transformedData = Object.entries(response?.data?.response).map(([country, functionalities]) => ({
                        name: country,
                        subItems: functionalities
                    }));

                    setCountries(transformedData);

                    // Initialize expanded state
                    const initialExpanded = {};
                    transformedData.forEach((country, index) => {
                        initialExpanded[country.name] = index === 0;
                    });
                    setExpanded(initialExpanded);

                } catch (error) {
                    setSeverity("error");
                    setMessage(error?.response?.data?.message || "Error fetching country data");
                    setOpenAlert(true);
                } finally {
                    setFetchLoading(false);
                }
            }
        };

        fetchCountryFunctionality();
    }, []);

    const handleRadioChange = (countryName) => {
        setSelectedCountry(countryName);
    };

    const handleExpand = (countryName) => {
        setExpanded(prev => ({
            ...prev,
            [countryName]: !prev[countryName]
        }));
    };

    const handleSubmit = async () => {
        if (!selectedCountry) {
            setSeverity("error");
            setMessage("Please select a country");
            setOpenAlert(true);
            return;
        }

        setLoading(true);
        try {
            const selectedCountryData = countries.find(country => country.name === selectedCountry);

            // Call the auto-assign API
            const response = await RoleService.autoAssignFunctionalities(
                state?.roleId, // Make sure roleId is passed in navigation state
                selectedCountryData.name
            );

            if (response.status === 200 || response.status === 201) {
                setSeverity("success");
                setMessage("Assigned Successfully");
                setOpenAlert(true);
                navigate('/roleAssignList');
            }
        } catch (error) {
            setSeverity("error");
            setMessage(error?.response?.data?.message || error?.response?.data?.errorMessage || error?.message);
            setOpenAlert(true);
        } finally {
            setLoading(false);
        }
    };

    // if (fetchLoading) {
    //     return (
    //         <LayoutAccessManagement title="Access Management">
    //             <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
    //                 <CircularProgress />
    //             </Box>
    //         </LayoutAccessManagement>
    //     );
    // }

    return (
        <LayoutAccessManagement title="Access Management">
            <Card sx={{
                backgroundColor: "#fff",
                borderRadius: '16px',
                p: 0,
                width: '500px',
                margin: '40px auto',
            }}>

                <Box sx={{ p: 0, pb: 0 }}>
                    {countries.length === 0 && !fetchLoading ? (
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100px',
                            color: '#666',
                            fontSize: '14px'
                        }}>
                            No country data available
                        </Box>
                    ) : (
                        countries.map((country, index) => (
                            <Box key={country.name}>
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
                                    borderBottom: '1px solid #E0E0E0',
                                    height: '48px',
                                    px: 2
                                }}>
                                    <FormControlLabel
                                        control={
                                            <Radio
                                                checked={selectedCountry === country.name}
                                                onChange={() => handleRadioChange(country.name)}
                                                sx={{
                                                    color: '#102A63',
                                                    '&.Mui-checked': {
                                                        color: '#102A63',
                                                    }
                                                }}
                                            />
                                        }
                                        label={country.name}
                                        sx={{
                                            flex: 1,
                                            m: 0,
                                            height: '100%',
                                            '& .MuiTypography-root': {
                                                fontSize: '14px'
                                            }
                                        }}
                                    />
                                    <IconButton
                                        onClick={() => handleExpand(country.name)}
                                        size="small"
                                        edge="end"
                                    >
                                        {expanded[country.name] ?
                                            <KeyboardArrowDownIcon /> :
                                            <KeyboardArrowRightIcon />
                                        }
                                    </IconButton>
                                </Box>
                                {expanded[country.name] && country.subItems.length > 0 && (
                                    <Box sx={{
                                        borderLeft: '1px solid #e0e0e0',
                                        width: '100%'
                                    }}>
                                        {country.subItems.map((item, idx) => (
                                            <Box
                                                key={item}
                                                sx={{
                                                    height: '48px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    pl: 11,
                                                    pr: 4,
                                                    color: '#666',
                                                    fontSize: '14px',
                                                    borderBottom: '1px solid #E0E0E0',
                                                    width: '100%'
                                                }}
                                            >
                                                {item}
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        )))}
                </Box>
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 2,
                    p: 4,
                }}>
                    <Button
                        onClick={() => navigate(-1)}
                        sx={{
                            backgroundColor: '#737D86',
                            color: 'white',
                            borderRadius: '8px',
                            px: 4,
                            height: '54px',
                            minHeight: '54px',
                            width: '130px',
                            minWidth: '130px',
                            '&:hover': {
                                backgroundColor: '#737D86',
                            }
                        }}
                    >
                        BACK
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={loading || !selectedCountry || fetchLoading}
                        sx={{
                            backgroundColor: '#FF6B00',
                            color: 'white',
                            borderRadius: '8px',
                            px: 4,
                            height: '54px',
                            minHeight: '54px',
                            width: '156px',
                            minWidth: '156px',
                            '&:hover': {
                                backgroundColor: '#FF6B00',
                            }
                        }}
                    >
                        {loading ? "Saving..." : "SUBMIT"}
                    </Button>
                </Box>
            </Card>
        </LayoutAccessManagement>
    );
};

export default CountrySelection;